import {
  AnyAction,
  createSelector,
  createSlice,
  PayloadAction
} from "@reduxjs/toolkit";

import { AppError } from "@/jenesys-types/errors";
import { Client, Company, User } from "@/jenesys-types/jenesys-entities/config";

// eslint-disable-next-line no-shadow
export enum BootstrapFailureReason {
  FatalError = "FATAL_ERROR",
  NoCompanysAvailable = "NO_COMPANIES_AVAILABLE",
  CompanyDataLoadingFailure = "COMPANY_DATA_LOADING_FAILURE",
  InvalidCompanyId = "INVALID_COMPANY_ID"
}

export interface BootstrapState {
  isBootstrapping: boolean;
  minorErrors: Array<AppError>;
  storedCompanies: Array<Company>;
  storedClients: Array<Client>;
  user?: User;
  isUserUpdating: boolean;
  isAcceptingTermsOfService: boolean;
  updateError?: AppError;
  acceptError?: AppError;
  bootstrapFailureError?: AppError;
  bootstrapFailureReason?: BootstrapFailureReason;
  isSettingUpHubSpot: boolean;
  hubSpotSetupError?: AppError;
}

interface BootstrapFailurePayload {
  reason: BootstrapFailureReason;
  error?: AppError;
  companyId?: string;
}
export type BootstrapFailureAction = PayloadAction<BootstrapFailurePayload>;

interface BootstrapSuccessPayload {
  companyId: string;
  user: User;
  errors: Array<AppError>;
}
export type BootstrapSuccessAction = PayloadAction<BootstrapSuccessPayload>;

interface BootstrapClientSuccessPayload {
  client: Client;
}
export type BootstrapClientSuccessAction =
  PayloadAction<BootstrapClientSuccessPayload>;

interface UpdateUserPayload {
  user: User;
  onSuccess?: () => void;
}
export type UpdateUserAction = PayloadAction<UpdateUserPayload>;

interface UpdateUserSuccessPayload {
  user: User;
}
export type UpdateUserSuccessAction = PayloadAction<UpdateUserSuccessPayload>;

interface UpdateUserFailurePayload {
  error?: AppError;
}
export type UpdateUserFailureAction = PayloadAction<UpdateUserFailurePayload>;

interface AcceptTermsofServicePayload {
  value: { agreed_tc: string | null };
  onSuccess?: () => void;
}
export type AcceptTermsofServiceAction =
  PayloadAction<AcceptTermsofServicePayload>;

interface AcceptTermsofServiceSuccessPayload {
  value: { agreed_tc: string | null };
}
export type AcceptTermsofServiceSuccessAction =
  PayloadAction<AcceptTermsofServiceSuccessPayload>;

interface AcceptTermsofServiceFailurePayload {
  error?: AppError;
}
export type AcceptTermsofServiceFailureAction =
  PayloadAction<AcceptTermsofServiceFailurePayload>;

interface SetupHubSpotPayload {
  onSuccess?: () => void;
}
export type SetupHubSpotAction = PayloadAction<SetupHubSpotPayload>;

interface SetupHubSpotSuccessPayload {
  email: string;
  token: string;
}
export type SetupHubSpotSuccessAction =
  PayloadAction<SetupHubSpotSuccessPayload>;

interface SetupHubSpotFailurePayload {
  error?: AppError;
}
export type SetupHubSpotFailureAction =
  PayloadAction<SetupHubSpotFailurePayload>;

interface ClearHubSpotCachePayload {}
export type ClearHubSpotCacheAction = PayloadAction<ClearHubSpotCachePayload>;

const name = "app/bootstrap";

export const initialState: BootstrapState = {
  isBootstrapping: true,
  storedCompanies: [],
  storedClients: [],
  minorErrors: [],
  user: undefined,
  isUserUpdating: false,
  isAcceptingTermsOfService: false,
  updateError: undefined,
  acceptError: undefined,
  isSettingUpHubSpot: false,
  hubSpotSetupError: undefined
};

const appSlice = createSlice({
  name,
  initialState,
  reducers: {
    bootstrapApp: (_state: BootstrapState) => ({
      ...initialState,
      isBootstrapping: true
    }),
    bootstrapFailure: {
      reducer: (state: BootstrapState, action: BootstrapFailureAction) => {
        state.bootstrapFailureError = action.payload.error;
        state.bootstrapFailureReason = action.payload.reason;
        state.isBootstrapping = false;
      },
      prepare: (
        reason: BootstrapFailureReason,
        error?: AppError,
        companyId?: string
      ) => ({
        payload: { error, companyId, reason }
      })
    },
    bootstrapSuccess: {
      reducer: (state: BootstrapState, action: BootstrapSuccessAction) => {
        state.minorErrors = action.payload.errors;
        state.isBootstrapping = false;
        state.user = action.payload.user;
        state.storedCompanies = state.user.companies;
      },
      prepare: (companyId: string, user: User, errors: Array<AppError>) => ({
        payload: { errors, user, companyId }
      })
    },
    bootstrapClientSuccess: {
      reducer: (
        state: BootstrapState,
        action: BootstrapClientSuccessAction
      ) => {
        state.storedClients = [action.payload.client];
      },
      prepare: (client: Client) => ({
        payload: { client }
      })
    },
    updateUser: {
      reducer: (state: BootstrapState, _action: UpdateUserAction) => {
        state.isUserUpdating = true;
      },
      prepare: (user: User, onSuccess?: () => void) => ({
        payload: { user, onSuccess }
      })
    },
    updateUserFailure: {
      reducer: (state: BootstrapState, action: UpdateUserFailureAction) => {
        state.updateError = action.payload.error;
        state.isUserUpdating = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    updateUserSuccess: {
      reducer: (state: BootstrapState, action: UpdateUserSuccessAction) => {
        state.isUserUpdating = false;
        state.user = action.payload.user;
      },
      prepare: (user: User) => ({
        payload: { user }
      })
    },
    acceptTermsOfService: {
      reducer: (state: BootstrapState, _action: AcceptTermsofServiceAction) => {
        state.isAcceptingTermsOfService = true;
      },
      prepare: (
        value: { agreed_tc: string | null },
        onSuccess?: () => void
      ) => ({
        payload: { value, onSuccess }
      })
    },
    acceptTermsOfServiceFailure: {
      reducer: (
        state: BootstrapState,
        action: AcceptTermsofServiceFailureAction
      ) => {
        state.acceptError = action.payload.error;
        state.isAcceptingTermsOfService = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    acceptTermsOfServiceSuccess: {
      reducer: (
        state: BootstrapState,
        action: AcceptTermsofServiceSuccessAction
      ) => {
        state.isAcceptingTermsOfService = false;
        state.user!!.agreed_tc = action.payload.value.agreed_tc;
      },
      prepare: (value: { agreed_tc: string | null }) => ({
        payload: { value }
      })
    },
    setupHubSpot: {
      reducer: (state: BootstrapState, _action: SetupHubSpotAction) => {
        state.isSettingUpHubSpot = true;
      },
      prepare: (onSuccess?: () => void) => ({
        payload: { onSuccess }
      })
    },
    setupHubSpotSuccess: {
      reducer: (state: BootstrapState, _action: SetupHubSpotSuccessAction) => {
        state.isSettingUpHubSpot = false;
        state.hubSpotSetupError = undefined;
      },
      prepare: (email: string, token: string) => ({
        payload: { email, token }
      })
    },
    setupHubSpotFailure: {
      reducer: (state: BootstrapState, action: SetupHubSpotFailureAction) => {
        state.isSettingUpHubSpot = false;
        state.hubSpotSetupError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    clearHubSpotCache: {
      reducer: (state: BootstrapState, _action: ClearHubSpotCacheAction) => {
        state.hubSpotSetupError = undefined;
      },
      prepare: () => ({
        payload: {}
      })
    }
  }
});

export const {
  bootstrapApp,
  bootstrapFailure,
  bootstrapSuccess,
  bootstrapClientSuccess,
  updateUser,
  updateUserFailure,
  updateUserSuccess,
  acceptTermsOfService,
  acceptTermsOfServiceFailure,
  acceptTermsOfServiceSuccess,
  setupHubSpot,
  setupHubSpotSuccess,
  setupHubSpotFailure,
  clearHubSpotCache
} = appSlice.actions;

// When sagas want to listen for the bootstrapSuccess action, they likely only
// want to run when the company ID is a non-empty string.
export const isBootstrapSuccessActionWithCompanyId = (action: AnyAction) =>
  action.type === bootstrapSuccess.type &&
  !!(action as BootstrapSuccessAction).payload.companyId;

// Selectors
const selectBootstrapState = (state: {
  app: { bootstrap: BootstrapState };
}): BootstrapState => state.app.bootstrap;

export const selectCompanies = createSelector(
  selectBootstrapState,
  (state) => state.storedCompanies
);

export const selectStoredClients = createSelector(
  selectBootstrapState,
  (state) => state.storedClients
);

export const selectBootstrapFailureError = createSelector(
  selectBootstrapState,
  (state) => state.bootstrapFailureError
);

export const selectBootstrapFailureReason = createSelector(
  selectBootstrapState,
  (state) => state.bootstrapFailureReason
);

export const selectMinorBootstrapErrors = createSelector(
  selectBootstrapState,
  (state) => state.minorErrors
);

export const selectIsBootstrapping = createSelector(
  selectBootstrapState,
  (state) => state.isBootstrapping
);

export const selectCurrentUser = createSelector(
  selectBootstrapState,
  (state) => state.user
);

export const selectIsUserUpdating = createSelector(
  selectBootstrapState,
  (state) => state.isUserUpdating
);

export const selectisAcceptingTermsOfService = createSelector(
  selectBootstrapState,
  (state) => state.isAcceptingTermsOfService
);

export const selectIsSettingUpHubSpot = createSelector(
  selectBootstrapState,
  (state) => state.isSettingUpHubSpot
);

export const selectHubSpotSetupError = createSelector(
  selectBootstrapState,
  (state) => state.hubSpotSetupError
);

export default appSlice.reducer;
